/**
 * AnalyticsErrorBoundary Component
 *
 * Error boundary specifically for analytics sections.
 * Catches render errors and displays a friendly retry UI.
 */

import { Component, ReactNode } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class AnalyticsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Analytics error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  override render() {
    if (this.state.hasError) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <ErrorOutline sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {this.props.fallbackTitle || 'Something went wrong'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We couldn't load this section. Please try again.
          </Typography>
          <Button variant="outlined" startIcon={<Refresh />} onClick={this.handleRetry}>
            Retry
          </Button>
        </Paper>
      );
    }

    return this.props.children;
  }
}

/**
 * Inline error display for non-critical errors
 * Use this for individual chart failures within a section
 */
export function InlineError({
  message = 'Failed to load',
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        color: 'text.secondary',
      }}
    >
      <ErrorOutline sx={{ fontSize: 32, mb: 1, color: 'error.light' }} />
      <Typography variant="body2" sx={{ mb: onRetry ? 2 : 0 }}>
        {message}
      </Typography>
      {onRetry && (
        <Button size="small" startIcon={<Refresh />} onClick={onRetry}>
          Retry
        </Button>
      )}
    </Box>
  );
}
